import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

//redux
import { useSelector, useDispatch } from 'react-redux';
import Filter, { getFilters } from '../../Components/Common/Filter';

import { createSelector } from 'reselect';
import { withTranslation } from 'react-i18next';
import { getAccountTransactions } from '../../slices/account-transaction/thunk';
import { formatCurrency, formatUtcDate } from '../../helpers/format';
import { isEmpty } from 'lodash';
import TableAccountTransactionManagement from './components/TableAccountTransactionManagement';
import useQsParams from '../../Components/Hooks/QueryString';
import { indetifierTypeTranslations, translations } from './action';
import CreateAccountTransaction from './components/CreateAccountTransaction';
import { toast } from 'react-toastify';

const AccountTransactionManagement = (props) => {
  const dispatch = useDispatch();
  const { searchParams, setSearchParams } = useQsParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const authUser = JSON.parse(sessionStorage.getItem('authUser') ?? '{}');
  const isAdmin =
    !!authUser?.isAdmin ||
    authUser?.role?.name === 'Administrador' ||
    authUser?.role?.name === 'AdministradorOckCash' ||
    authUser?.roles?.[0]?.name === 'Administrador' ||
    authUser?.roles?.[0]?.name === 'AdministradorOckCash';

  const AccountTransactionState = createSelector(
    (state) => state.AccountTransaction.accountTransactions,
    (accountTransactions) => accountTransactions,
  );

  const loadingState = createSelector(
    (state) => state.AccountTransaction.isLoading,
    (isLoading) => isLoading,
  );

  const metaState = createSelector(
    (state) => state.AccountTransaction.meta,
    (meta) => meta,
  );

  const accountTransactions = useSelector(AccountTransactionState);
  const isLoading = useSelector(loadingState);
  const meta = useSelector(metaState);

  useEffect(() => {
    getData();
  }, [dispatch, searchParams.toString()]);

  const handleAccountTransaction = () => {
    getData();
    toast.success(props.t('account-transaction-management.create-success'));
  };

  const accountTransactionType = [
    {
      label: props.t(`account-transaction-management.type.table.credit`),
      value: 'credit',
    },
    {
      label: props.t(`account-transaction-management.type.table.debit`),
      value: 'debit',
    },
  ];

  const accountTransactionIdentifierType = [
    {
      label: props.t(`account-transaction-management.identifier-type.table.wallet_withdrawal`),
      value: 'wallet_withdrawal',
    },
    {
      label: props.t(`account-transaction-management.identifier-type.table.manual_operation`),
      value: 'manual_operation',
    },
  ];

  const getTranslationAccountTransactionType = (action) => {
    return (
      translations[action] ?? {
        label: 'Não mapeado',
        color: 'esquecémo-dinuevo',
      }
    );
  };

  const getTranslationAccountTransactionIdentifierType = (action) => {
    return (
      indetifierTypeTranslations[action] ?? {
        label: 'Não mapeado',
        color: 'esquecémo-dinuevo',
      }
    );
  };

  const getData = () => {
    const params = { page: searchParams.get('page') ?? 1 };
    const appliedFilters = getFilters([], [
      { field: 'identifier', type: 'like' },
      { field: 'identifier_type', type: 'eq-as-raw' },
      { field: 'type', type: 'eq-as-raw' },
      { field: 'amount', type: 'between' },
      { field: 'created_at', type: 'date' },
    ]);

    if (!isEmpty(appliedFilters)) {
      params.filters = appliedFilters;
    }

    dispatch(getAccountTransactions(params));
  };

  const filters = [
    {
      field: 'amount',
      name: 'Valor',
      type: 'range',
    },
  ];

  const columns = useMemo(
    () => [
      {
        Header: props.t('account-transaction-management.table.identifier'),
        Cell: (props) => props.row.original.identifier || '-',
        reportProps: {
          accessor: 'identifier',
        },
      },
      {
        Header: props.t('account-transaction-management.table.identifier-type'),
        Cell: (props) => {
          const translation = getTranslationAccountTransactionIdentifierType(props.row.original.identifier_type);

          return (
            <React.Fragment>
              <span className={`badge rounded-pill ${translation.color}`}>{translation.label}</span>
            </React.Fragment>
          );
        },
        reportProps: {
          accessor: 'identifier_type',
        },
      },
      {
        Header: props.t('account-transaction-management.table.type'),
        accessor: 'type',
        filterable: false,
        Cell: (props) => {
          const translation = getTranslationAccountTransactionType(props.row.original.type);

          return (
            <React.Fragment>
              <span className={`badge rounded-pill ${translation.color}`}>{translation.label}</span>
            </React.Fragment>
          );
        },
      },
      {
        Header: props.t('account-transaction-management.table.amount'),
        accessor: 'amount',
        filterable: false,
        Cell: (props) => formatCurrency(props.row.original.amount),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: props.t('account-transaction-management.table.created_at'),
        accessor: 'date',
        filterable: false,
        Cell: (props) => formatUtcDate(props.row.original.created_at),
        reportProps: {
          formatter: {
            type: 'date',
            properties: { tz: 'America/Sao_Paulo', format: 'DD/MM/YYYY' },
          },
        },
      },
    ],
    [],
  );

  document.title = 'Gestão de Transações da Empressa | Opencashback';

  if (!isAdmin) {
    return <Navigate to="/resultados/vendas" />;
  }

  return (
    <React.Fragment>
      <TableAccountTransactionManagement
        props={props}
        columns={columns}
        searchParams={searchParams}
        setShowCreateModal={setShowCreateModal}
        isLoading={isLoading}
        accountTransactions={accountTransactions}
        accountTransactionType={accountTransactionType}
        accountTransactionIdentifierType={accountTransactionIdentifierType}
        meta={meta}
        page={searchParams.get('page')}
        setPage={(p) => setSearchParams('page', p)}
      />
      <Filter
        filters={filters}
        values={[
          {
            "field": "amount",
            "type": "between",
            "values": [
              "0",
              "1000000000"
            ],
            "group": "value-range"
          },
        ]}
        localePrefix="account-transaction-management"
        useGroups
      />
      <CreateAccountTransaction
        isOpen={showCreateModal}
        toggle={() => setShowCreateModal(!showCreateModal)}
        onSuccess={handleAccountTransaction}
        accountTransactionType={accountTransactionType}
      />
    </React.Fragment>
  );
};

export default withTranslation()(AccountTransactionManagement);
